import Link from 'next/link'
import Image from 'next/image'
import type { Tenant } from '@sifly/shared-types'
import type { ChromeHeaderData } from './SiteHeader'
import { LogoContrastPlate } from './LogoContrastPlate'

// Chrome v2 — `minimal_ad_landing` header. Stripped layout for paid-ad
// landing pages where the goal is single-CTA conversion: logo + optional
// CTA only. No primary nav, no utility bar, no search/language slots —
// the page itself is the funnel.
//
// Anti-slop guardrails (per Design Pass 4, SiteSmith Builder Rethink):
//   • Logo is LEFT-ALIGNED, not centered. Centering signals "marketing
//     site" — the opposite of focused ad-landing.
//   • CTA uses the tenant's brand accent (`--brand-primary`); no
//     hardcoded blue.
//   • Honors `--brand-font-heading` / `--brand-font-body` like every
//     other variant. No bespoke typography.
//   • No icons-in-colored-circles, no decorative blobs, no gradients.

type MinimalAdLandingHeaderProps = {
  tenant: Tenant
  logoUrl?: string | null
  /** Contrast-plate guard — see SiteHeader.logoPlate. */
  logoPlate?: boolean
  data: ChromeHeaderData
}

const LOGO_HEIGHT_BY_SIZE = { sm: 'h-6', md: 'h-8', lg: 'h-12', xl: 'h-14 md:h-20' } as const

function ctaClasses(style: 'primary' | 'secondary' | 'ghost'): string {
  switch (style) {
    case 'primary':
      return 'inline-flex items-center px-4 h-9 rounded-md bg-[var(--brand-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity'
    case 'secondary':
      return 'inline-flex items-center px-4 h-9 rounded-md border border-[var(--color-border)] text-[var(--color-text-primary)] text-sm font-medium hover:bg-[var(--color-bg-secondary)] transition-colors'
    case 'ghost':
      return 'inline-flex items-center px-3 h-9 text-[var(--color-text-primary)] text-sm font-medium hover:opacity-80 transition-opacity'
  }
}

export function MinimalAdLandingHeader({ tenant, logoUrl, logoPlate, data }: MinimalAdLandingHeaderProps) {
  const logoSize = data.logo_size ?? 'md'
  const cta = data.cta
  const heightCls = LOGO_HEIGHT_BY_SIZE[logoSize]
  return (
    <header
      className="border-b border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]"
      data-component="SiteHeader"
      data-variant="minimal_ad_landing"
      data-section-ref="chrome:header:minimal_ad_landing"
    >
      <div className={`mx-auto max-w-[var(--max-width-page)] px-4 ${logoSize === 'xl' ? 'min-h-16 py-2' : 'h-16'} flex items-center justify-between`}>
        {/* Left-aligned logo — anti-slop guardrail (no centered logo). */}
        <Link href="/" className="shrink-0" data-chrome-target="header:logo">
          {logoUrl ? (
            <LogoContrastPlate plate={logoPlate}>
              <Image
                src={logoUrl}
                alt={tenant.name}
                width={120}
                height={32}
                className={`${heightCls} w-auto object-contain`}
              />
            </LogoContrastPlate>
          ) : (
            <span
              className="font-semibold text-lg text-[var(--color-text-primary)]"
              style={{ fontFamily: 'var(--brand-font-heading, var(--font-heading))' }}
            >
              {tenant.name}
            </span>
          )}
        </Link>
        {/* Optional single CTA — uses tenant brand accent. No nav, no
            utility bar, nothing else. */}
        {cta?.enabled && (
          <Link href={cta.href} className={ctaClasses(cta.style)} data-chrome-target="header:cta">
            {cta.label}
          </Link>
        )}
      </div>
    </header>
  )
}
